import React, { useMemo, useState, useEffect } from "react";
import { useTable, usePagination, useGlobalFilter, useAsyncDebounce, useSortBy } from "react-table";
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, IconButton, Collapse, Box, styled, Autocomplete } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Icon from "@mui/material/Icon";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDPagination from "components/MDPagination";

// // Styled TableRow component
// const CustomTableRow = styled(TableRow)(({ theme }) => ({
//   color: "inherit",
//   display: "flex",
//   verticalAlign: "middle",
//   outline: 0,
// }));

// // Sample Data
// const data = [
//   {
//     Message: [
//       {
//         IdRequest: 170,
//         Title: "dfg",
//         Description: "dfg",
//         companyName: "Pagac, Hirthe and Hilpert",
//         CategoryName: "Offerta",
//         TypeName: "Finanza Agevolata",
//         PublishDate: "2025-02-28",
//       },
//     ],
//     CreationUser: "Stefano asd",
//   },
//   {
//     Message: [
//       {
//         IdRequest: 171,
//         Title: "asd",
//         Description: "asd",
//         companyName: "Pagac, Hirthe and Hilpert",
//         CategoryName: "Offerta",
//         TypeName: "Finanza Agevolata",
//         PublishDate: "2025-03-02",
//       },
//     ],
//     CreationUser: "Stefano asd",
//   },
//   {
//     Message: [
//       {
//         IdRequest: 155,
//         Title: "asd",
//         Description: "asd",
//         companyName: "Pagac, Hirthe and Hilpert",
//         CategoryName: "Offerta",
//         TypeName: "Finanza Agevolata",
//         PublishDate: "2025-02-22",
//       },
//     ],
//     CreationUser: "Stefano asd",
//   },
// ];

// MessageCollapse component for handling the collapsible message details
// const MessageCollapse = ({ row }) => {
//   const [open, setOpen] = useState(false);

//   const toggleOpen = () => {
//     setOpen(!open);
//   };

//   return (
//     <Box>
//       <IconButton onClick={toggleOpen}>
//         <ExpandMoreIcon />
//       </IconButton>
//       <Collapse in={open}>
//         {row.values.Message.map((msg, index) => (
//           <Box sx={{ marginTop: 2 }}>
//             <TableContainer sx={{ boxShadow: "none", width: "100%" }}>
//               <Table sx={{ minWidth: "650px", display: "block" }}>
//                 <TableHead>
//                   <TableRow>
//                     <TableCell sx={{ fontWeight: "bold", textAlign: "center" }}>IdRequest</TableCell>
//                     <TableCell sx={{ fontWeight: "bold", textAlign: "center" }}>Title</TableCell>
//                     <TableCell sx={{ fontWeight: "bold", textAlign: "center" }}>Description</TableCell>
//                     <TableCell sx={{ fontWeight: "bold", textAlign: "center" }}>Company Name</TableCell>
//                     <TableCell sx={{ fontWeight: "bold", textAlign: "center" }}>Category</TableCell>
//                     <TableCell sx={{ fontWeight: "bold", textAlign: "center" }}>Type</TableCell>
//                     <TableCell sx={{ fontWeight: "bold", textAlign: "center" }}>Publish Date</TableCell>
//                   </TableRow>
//                 </TableHead>
//                 <TableBody>
//                   <TableRow>
//                     <TableCell sx={{ textAlign: "center" }}>{msg.IdRequest}</TableCell>
//                     <TableCell sx={{ textAlign: "center" }}>{msg.Title}</TableCell>
//                     <TableCell sx={{ textAlign: "center" }}>{msg.Description}</TableCell>
//                     <TableCell sx={{ textAlign: "center" }}>{msg.companyName}</TableCell>
//                     <TableCell sx={{ textAlign: "center" }}>{msg.CategoryName}</TableCell>
//                     <TableCell sx={{ textAlign: "center" }}>{msg.TypeName}</TableCell>
//                     <TableCell sx={{ textAlign: "center" }}>{msg.PublishDate}</TableCell>
//                   </TableRow>
//                 </TableBody>
//               </Table>
//             </TableContainer>
//           </Box>
//         ))}
//       </Collapse>
//     </Box>
//   );
// };


// DataTable component
const DataTable = ({ entriesPerPage, canSearch, showTotalEntries, table, pagination, isSorted, noEndBorder, currentPageIndex, setCurrentPageIndex }) => {
  const defaultValue = entriesPerPage.defaultValue || 10;
  const entries = entriesPerPage.entries ? entriesPerPage.entries.map((el) => el.toString()) : ["5", "10", "15", "20", "25", "50"];
  const columns = useMemo(() => table.columns, [table]);
  const data = useMemo(() => table.rows, [table]);

  const tableInstance = useTable(
    { columns, data, initialState: { pageIndex: currentPageIndex } },
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    rows,
    page,
    pageOptions,
    canPreviousPage,
    canNextPage,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    setGlobalFilter,
    state: { pageIndex, pageSize, globalFilter },
  } = tableInstance;

  useEffect(() => {
    localStorage.setItem("pageIndex", pageIndex);
    setCurrentPageIndex(pageIndex);
  }, [pageIndex]);

  useEffect(() => {
    localStorage.setItem("pageSize", pageSize);
  }, [pageSize]);

  useEffect(() => setPageSize(defaultValue || 10), [defaultValue]);

  const setEntriesPerPage = (value) => setPageSize(value);

  const renderPagination = pageOptions.map((option) => (
    <MDPagination item key={option} onClick={() => gotoPage(Number(option))} active={pageIndex === option}>
      {option + 1}
    </MDPagination>
  ));

  const handleInputPagination = ({ target: { value } }) => (value > pageOptions.length || value < 0 ? gotoPage(0) : gotoPage(Number(value)));

  const customizedPageOptions = pageOptions.map((option) => option + 1);

  const handleInputPaginationValue = ({ target: value }) => gotoPage(Number(value.value - 1));

  const [search, setSearch] = useState(globalFilter);

  const onSearchChange = useAsyncDebounce((value) => {
    setGlobalFilter(value || undefined);
  }, 100);

  const setSortedValue = (column) => {
    let sortedValue;

    if (isSorted && column.isSorted) {
      sortedValue = column.isSortedDesc ? "desc" : "asce";
    } else if (isSorted) {
      sortedValue = "none";
    } else {
      sortedValue = false;
    }

    return sortedValue;
  };

  const entriesStart = pageIndex === 0 ? pageIndex + 1 : pageIndex * pageSize + 1;

  let entriesEnd;

  if (pageIndex === 0) {
    entriesEnd = pageSize;
  } else if (pageIndex === pageOptions.length - 1) {
    entriesEnd = rows.length;
  } else {
    entriesEnd = pageSize * (pageIndex + 1);
  }

  return (
    <TableContainer sx={{ boxShadow: "none" }}>
      {entriesPerPage || canSearch ? (
        <MDBox display="flex" justifyContent="space-between" alignItems="center" p={3}>
          {entriesPerPage && (
            <MDBox display="flex" alignItems="center">
              <Autocomplete
                disableClearable
                value={pageSize.toString()}
                options={entries}
                onChange={(event, newValue) => {
                  setEntriesPerPage(parseInt(newValue, 10));
                }}
                size="small"
                sx={{ width: "5rem" }}
                renderInput={(params) => <MDInput {...params} />}
              />
              <MDTypography variant="caption" color="secondary">
                &nbsp;&nbsp;elementi per pagina
              </MDTypography>
            </MDBox>
          )}
          {canSearch && (
            <MDBox width="20rem" mr="auto">
              <MDInput
                placeholder="Search..."
                value={search}
                size="small"
                fullWidth
                onChange={({ currentTarget }) => {
                  setSearch(search);
                  onSearchChange(currentTarget.value);
                }}
              />
            </MDBox>
          )}
        </MDBox>
      ) : null}
      <Table {...getTableProps()}>
        <MDBox component="thead">
          {headerGroups.map((headerGroup, key) => (
            <TableRow key={key} {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map((column, idx) => (
                <TableCell key={idx} {...column.getHeaderProps(isSorted && column.getSortByToggleProps())} sx={{ textAlign: "center" }}>
                  {column.render("Header")}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </MDBox>
        <TableBody {...getTableBodyProps()}>
          {page.map((row, key) => {
            prepareRow(row);
            return (
              <TableRow key={key} {...row.getRowProps()}>
                {row.cells.map((cell, idx) => (
                  <TableCell key={idx} sx={{ textAlign: "center" }}>
                    {cell.render("Cell")}
                  </TableCell>
                ))}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>

      <MDBox display="flex" flexDirection={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "flex-start", sm: "center" }} p={3}>
        {showTotalEntries && (
          <MDBox mb={{ xs: 3, sm: 0 }}>
            <MDTypography variant="button" color="secondary" fontWeight="regular">
              Showing {entriesStart} to {entriesEnd} of {rows.length} entries
            </MDTypography>
          </MDBox>
        )}
        {pageOptions.length > 1 && (
          <MDPagination variant="gradient" color="info">
            {canPreviousPage && (
              <MDPagination item onClick={() => previousPage()}>
                <Icon sx={{ fontWeight: "bold" }}>chevron_left</Icon>
              </MDPagination>
            )}
            {renderPagination.length > 6 ? (
              <MDBox width="5rem" mx={1}>
                <MDInput
                  inputProps={{ type: "number" }}
                  onChange={handleInputPagination}
                  value={pageIndex + 1}
                />
              </MDBox>
            ) : (
              renderPagination
            )}
            {canNextPage && (
              <MDPagination item onClick={() => nextPage()}>
                <Icon sx={{ fontWeight: "bold" }}>chevron_right</Icon>
              </MDPagination>
            )}
          </MDPagination>
        )}
      </MDBox>
    </TableContainer>
  );
};

export default DataTable;